import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Calendar, cn, Popover, PopoverContent, PopoverTrigger } from '@ui/primitives';
import { format, isBefore, isWithinInterval, startOfDay } from 'date-fns';
import { CalendarIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import { TimePicker } from './TimePicker';
const DEFAULT_PLACEHOLDER = 'Pick a date';
const DEFAULT_DATE_FORMAT = 'PPP';
const DEFAULT_RANGE_FORMAT = 'MMM d, yyyy';
const DEFAULT_DATE_TIME_FORMAT = 'PPP p';
export function DatePicker(param) {
    let { date, onDateChange, dateRange, onDateRangeChange, placeholder = DEFAULT_PLACEHOLDER, dateFormat = DEFAULT_DATE_FORMAT, rangeFormat = DEFAULT_RANGE_FORMAT, disabled = false, className, disabledDates, showTimePicker = false, timePickerClassName } = param;
    var _ref;
    const [open, setOpen] = React.useState(false);
    const [pickingRange, setPickingRange] = React.useState(undefined);
    const [hoveredDate, setHoveredDate] = React.useState(undefined);
    const timeInputRef = React.useRef(null);
    const isRange = onDateRangeChange !== undefined;
    const showTime = showTimePicker && !isRange;
    const rangeSelection = React.useMemo(()=>{
        var _ref;
        return (_ref = pickingRange !== null && pickingRange !== void 0 ? pickingRange : dateRange) !== null && _ref !== void 0 ? _ref : undefined;
    }, [
        pickingRange,
        dateRange
    ]);
    const hoverRangeModifier = React.useMemo(()=>{
        const from = pickingRange === null || pickingRange === void 0 ? void 0 : pickingRange.from;
        if (!from || !hoveredDate) return undefined;
        const start = isBefore(from, hoveredDate) || from.toDateString() === hoveredDate.toDateString() ? startOfDay(from) : startOfDay(hoveredDate);
        const end = start.getTime() === startOfDay(from).getTime() ? startOfDay(hoveredDate) : startOfDay(from);
        const fromDay = startOfDay(from);
        return (date)=>{
            const d = startOfDay(date);
            if (d.getTime() === fromDay.getTime()) return false;
            return isWithinInterval(d, {
                start,
                end
            });
        };
    }, [
        pickingRange === null || pickingRange === void 0 ? void 0 : pickingRange.from,
        hoveredDate
    ]);
    React.useEffect(()=>{
        if (!open) {
            setPickingRange(undefined);
            setHoveredDate(undefined);
        }
    }, [
        open
    ]);
    const handleSingleSelect = React.useCallback((selected)=>{
        if (!selected) {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(undefined);
            return;
        }
        const merged = new Date(selected);
        if (showTime && date) {
            merged.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
        }
        onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(merged);
        if (!showTime) setOpen(false);
    }, [
        onDateChange,
        showTime,
        date
    ]);
    const handleRangeSelect = React.useCallback((range)=>{
        const from = range === null || range === void 0 ? void 0 : range.from;
        const to = range === null || range === void 0 ? void 0 : range.to;
        const hasFullRange = from && to && from.toDateString() !== to.toDateString();
        if (hasFullRange) {
            setPickingRange(undefined);
            setOpen(false);
            onDateRangeChange === null || onDateRangeChange === void 0 ? void 0 : onDateRangeChange({
                from,
                to
            });
        } else if (from) {
            setPickingRange({
                from
            });
        } else {
            setPickingRange(undefined);
        }
    }, [
        onDateRangeChange
    ]);
    const displayFormat = showTime ? DEFAULT_DATE_TIME_FORMAT : dateFormat;
    const displayText = React.useMemo(()=>{
        if (isRange && rangeSelection) {
            if (rangeSelection.from && rangeSelection.to) {
                return "".concat(format(rangeSelection.from, rangeFormat), " – ").concat(format(rangeSelection.to, rangeFormat));
            }
            if (rangeSelection.from) {
                return format(rangeSelection.from, rangeFormat);
            }
        }
        if (!isRange && date) {
            return format(date, displayFormat);
        }
        return null;
    }, [
        isRange,
        date,
        rangeSelection,
        rangeFormat,
        displayFormat
    ]);
    const handleTimeChange = React.useCallback((next)=>{
        onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(next);
    }, [
        onDateChange
    ]);
    const handleClear = React.useCallback(()=>{
        if (isRange) {
            setPickingRange(undefined);
            onDateRangeChange === null || onDateRangeChange === void 0 ? void 0 : onDateRangeChange(undefined);
        } else {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(undefined);
        }
        setOpen(false);
    }, [
        isRange,
        onDateChange,
        onDateRangeChange
    ]);
    const hasSelection = isRange ? !!((rangeSelection === null || rangeSelection === void 0 ? void 0 : rangeSelection.from) || (rangeSelection === null || rangeSelection === void 0 ? void 0 : rangeSelection.to) || (dateRange === null || dateRange === void 0 ? void 0 : dateRange.from) || (dateRange === null || dateRange === void 0 ? void 0 : dateRange.to)) : !!date;
    const disabledMatcher = React.useMemo(()=>disabledDates && disabledDates.length > 0 ? (d)=>disabledDates.some((disabled)=>d.toDateString() === disabled.toDateString()) : undefined, [
        disabledDates
    ]);
    return /*#__PURE__*/ _jsxs(Popover, {
        open: open,
        onOpenChange: setOpen,
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                className: "bg-input/30",
                children: /*#__PURE__*/ _jsxs(Button, {
                    variant: "outline",
                    disabled: disabled,
                    "data-empty": !displayText,
                    className: cn('relative pr-8', className),
                    style: {
                        minWidth: 280,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        fontWeight: 400
                    },
                    children: [
                        /*#__PURE__*/ _jsx(CalendarIcon, {
                            className: "mr-2 h-4 w-4 shrink-0 opacity-50"
                        }),
                        displayText !== null && displayText !== void 0 ? displayText : /*#__PURE__*/ _jsx("span", {
                            className: "text-muted-foreground",
                            children: placeholder
                        }),
                        hasSelection ? /*#__PURE__*/ _jsx("span", {
                            role: "button",
                            tabIndex: -1,
                            className: "absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 opacity-70 hover:opacity-100 hover:bg-accent",
                            onClick: (e)=>{
                                e.stopPropagation();
                                handleClear();
                            },
                            onKeyDown: (e)=>{
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleClear();
                                }
                            },
                            "aria-label": "Clear date",
                            children: /*#__PURE__*/ _jsx(XIcon, {
                                className: "h-4 w-4"
                            })
                        }) : null
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PopoverContent, {
                className: "w-auto border-input p-0",
                align: "start",
                onPointerDownOutside: showTime ? (e)=>{
                    if (timeInputRef.current && document.activeElement === timeInputRef.current) {
                        e.preventDefault();
                    }
                } : undefined,
                children: [
                    isRange ? /*#__PURE__*/ _jsx(Calendar, {
                        mode: "range",
                        defaultMonth: (_ref = rangeSelection === null || rangeSelection === void 0 ? void 0 : rangeSelection.from) !== null && _ref !== void 0 ? _ref : dateRange === null || dateRange === void 0 ? void 0 : dateRange.from,
                        selected: rangeSelection,
                        onSelect: handleRangeSelect,
                        onDayMouseEnter: (date)=>setHoveredDate(date),
                        onDayMouseLeave: ()=>setHoveredDate(undefined),
                        disabled: disabledMatcher,
                        numberOfMonths: 2,
                        modifiers: hoverRangeModifier ? {
                            range_preview: hoverRangeModifier
                        } : undefined,
                        modifiersClassNames: hoverRangeModifier ? {
                            range_preview: 'bg-accent'
                        } : undefined
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Calendar, {
                                mode: "single",
                                defaultMonth: date,
                                selected: date,
                                onSelect: handleSingleSelect,
                                disabled: disabledMatcher
                            }),
                            showTime && /*#__PURE__*/ _jsx(TimePicker, {
                                ref: timeInputRef,
                                date: date,
                                onTimeChange: handleTimeChange,
                                className: cn('border-t border-border bg-popover p-3', timePickerClassName)
                            })
                        ]
                    }),
                    hasSelection ? /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end border-t border-border p-2",
                        children: /*#__PURE__*/ _jsxs(Button, {
                            variant: "ghost",
                            size: "sm",
                            onClick: handleClear,
                            className: "gap-1.5 text-muted-foreground",
                            children: [
                                /*#__PURE__*/ _jsx(XIcon, {
                                    className: "h-3.5 w-3.5"
                                }),
                                "Clear"
                            ]
                        })
                    }) : null
                ]
            })
        ]
    });
}
